<template>
  <div v-if="allSections.length">
    <BaseButton
      :text="`Back to 'Your details'`"
      flat
      ripple
      class="font-weight-bold d-flex ml-n5"
      icon="mdi-chevron-left"
      color="#106175"
      small
      @click="$router.push({ name: 'YourDetailsPage' })"
    />

    <v-row class="ma-0 mt-10 mb-5">
      <v-col class="d-flex justify-space-between flex-wrap pa-0">
        <div>
          <h1 class="font-24 font-lg-28 font-weight-bold black2-color">
            Property details
          </h1>
        </div>
        <BaseButton
          :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
          flat
          depressed
          ripple
          class="font-weight-bold px-1 py-3 d-flex"
          icon="mdi-content-save"
          color="#106175"
          small
          @click="toggleGlobalSaveAndExitDialog"
        />
      </v-col>
    </v-row>

    <ValidationObserver v-slot="{ validate }">
      <form @submit.prevent="onSubmit(validate)">
        <LayoutAddressSearch
          lexisNexisFailedRequestMessage="We are not able to get your property details at this time. Please add your property details below."
          inputFindPropertyClass="mb-2"
          hideDetails="auto"
          :propertyDetailsTextForm="B2B_LANDLORD_PROPERTY_LABEL"
          :isShowDialogContentWarningEditAddressManual="false"
        />

        <BaseLabel
          class="mt-10"
          text="Are all windows fitted with key operated window locks?"
          helpText="Please select ‘Yes’ if all the windows to the property are secured by key operated window locks. If one or more windows are not key operated, please select ‘No’."
        />
        <BaseToggleButton
          class="mt-2"
          v-model="windowLocksFitted.Value"
          :toggleButtonText="{
            yes: 'Yes',
            yesDataUiid: 'WindowLocksFitted_yes',
            noDataUiid: 'WindowLocksFitted_no',
            no: 'No',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <BaseLabel
          class="mt-10"
          text="What type of burglar alarm does your property have?"
          helpText="Please specify whether the burglar alarm on the property is annually maintained by a professional, or if it is non-maintained. If there is an alarm fitted to the building that is not in use, select 'No Alarm'."
        />
        <BaseSelect
          class="mt-2"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '360px' : '100%',
          }"
          name="alarmType"
          :rules="{
            required: {
              required: true,
              fieldLabel: 'Alarm type',
            },
          }"
          dataUiid="AlarmType"
          v-model="alarmType.Value"
          :items="setItemDataUiids('AlarmType', ALARM_TYPE_ITEMS)"
          :isEnableSortOrderListItems="false"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          aria-label="Alarm type"
        />

        <BaseLabel
          class="mt-10"
          text="Will the property be left unoccupied for a total of more than 90 days in a row?"
          helpText="The customer must tell us if the property is not going to be lived in by any tenants for more than 90 days in a row. For a property to be 'lived in' it must be slept in frequently."
        />
        <BaseToggleButton
          class="mt-2"
          v-model="unoccupiedNinetyDays.Value"
          :toggleButtonText="{
            yes: 'Yes',
            yesDataUiid: 'UnoccupiedNinetyDays_yes',
            noDataUiid: 'UnoccupiedNinetyDays_no',
            no: 'No',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <BaseLabel
          class="mt-10"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '770px' : '100%',
          }"
          text="Is there work currently taking place on the building which is not routine, repair, maintenance or decoration?"
          helpText="The customer must tell us if there is currently any work taking place on the building such as structural work. This does not include routine repair work, maintenance or decoration."
        />
        <BaseToggleButton
          class="mt-2"
          v-model="nonRoutineBuildingWork.Value"
          :toggleButtonText="{
            yes: 'Yes',
            yesDataUiid: 'NonRoutineBuildingWork_yes',
            noDataUiid: 'NonRoutineBuildingWork_no',
            no: 'No',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <template v-if="nonRoutineBuildingWork.Value">
          <BaseLabel
            class="mt-10"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '770px' : '100%',
            }"
            text="What is the value of the work(s) taking place?"
            helpText="The customer must tell us the overall value of the building work being carried out."
          />
          <BaseInput
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '240px' : '100%',
            }"
            class="mt-2"
            :rules="{
              required: {
                required: true,
                fieldLabel: 'What is the value of the work(s) taking place?',
              },
              between: {
                min: 0.01,
                max: 999999.99,
                fieldLabel: 'Amount',
              },
            }"
            dataUiid="NonRoutineBuildingWorkValue"
            v-model="nonRoutineBuildingWorkValue.Value"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
            prefix="£"
            moneyFormat
            hide-details="auto"
            aria-label="What is the value of the work(s) taking place?"
          />

          <BaseLabel
            class="mt-10"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '770px' : '100%',
            }"
            text="Is there a JCT Contract in place covering the works?"
            helpText="We need to know whether the work being done is under a contract. For example, a Joint Contracts Tribunal (also called a JCT) will produce a contract for construction, guidance notes and other standard documentation."
          />
          <BaseToggleButton
            class="mt-2"
            v-model="nonRoutineBuildingWorkJctContract.Value"
            :toggleButtonText="{
              yes: 'Yes',
              yesDataUiid: 'NonRoutineBuildingWorkJctContract_yes',
              noDataUiid: 'NonRoutineBuildingWorkJctContract_no',
              no: 'No',
            }"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
          />
        </template>

        <BaseLabel
          class="mt-10"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '770px' : '100%',
          }"
          text="Will the property be let to Students?"
          helpText="You must tell us if the property will be let to students."
        />
        <BaseToggleButton
          class="mt-2"
          v-model="studentTenants.Value"
          :toggleButtonText="{
            yes: 'Yes',
            yesDataUiid: 'StudentTenants_yes',
            noDataUiid: 'StudentTenants_no',
            no: 'No',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <BaseLabel
          class="mt-10"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '770px' : '100%',
          }"
          text="Will the property be let to tenants funding rent through the benefits agency?"
          helpText="You must tell us if the property will be let to tenants funding their rent through the Department of Work and Pensions."
        />
        <BaseToggleButton
          class="mt-2"
          v-model="dssTenants.Value"
          :toggleButtonText="{
            yes: 'Yes',
            yesDataUiid: 'DssTenants_yes',
            noDataUiid: 'DssTenants_no',
            no: 'No',
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :min-width="$vuetify.breakpoint.lgAndUp ? '110' : ''"
        />

        <BaseLabel
          class="mt-10"
          text="How many years no claims discount do you have for buildings insurance?"
          helpText="Please select your current no claims discount entitlement for buildings insurance. This can be found on their previous insurer’s policy documentation. Customers can also transfer no claims discount from a Landlords buildings insurance policy that has been cancelled within the last 2 years."
        />
        <BaseRadio
          class="mt-2"
          psl-style="button"
          :rules="{
            required: {
              required: true,
              fieldLabel:
                'What is your current no claims discount for Buildings?',
            },
          }"
          :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
          :width="$vuetify.breakpoint.lgAndUp ? '72' : '100'"
          :style="{
            'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
          }"
          :items="
            setItemDataUiids('NoClaimsBuildings', NO_CLAIMS_BUILDINGS_ITEMS)
          "
          v-model="noClaimsBuildings.Value"
          aria-label="How many years no claims discount do you have for buildings insurance?"
        />

        <template v-if="!isBuildingsOnly">
          <BaseLabel
            class="mt-10"
            text="How many years no claims discount do you have for contents insurance?"
            helpText="Please select the customer's current no claims discount entitlement for contents insurance. This can be found on their previous insurer’s policy documentation. The customer can also transfer no claims discount from a Landlords buildings and contents insurance policy that has been cancelled within the last 2 years."
          />
          <BaseRadio
            :rules="{
              required: {
                required: true,
                fieldLabel:
                  'What is your current no claims discount for Contents?',
              },
            }"
            class="mt-2"
            psl-style="button"
            :height="$vuetify.breakpoint.lgAndUp ? '48' : '60'"
            :width="$vuetify.breakpoint.lgAndUp ? '72' : '100'"
            :style="{
              'max-width': $vuetify.breakpoint.lgAndUp ? '440px' : '100%',
            }"
            :items="
              setItemDataUiids('NoClaimsContents', NO_CLAIMS_CONTENTS_ITEMS)
            "
            v-model="noClaimsContents.Value"
            aria-label="How many years no claims discount do you have for contents insurance?"
          />
        </template>

        <BaseButton
          depressed
          name="add"
          text="GET QUOTES"
          rounded
          :block="$vuetify.breakpoint.lgAndUp ? false : true"
          dense
          :color="'psorange'"
          class="font-weight-bold font-20 pa-7 px-13 mb-3 mt-10"
          type="submit"
          :disabled="disableContinue"
          aria-label="Get quotes"
        />
      </form>
    </ValidationObserver>

    <EligibilityBaseDialog ref="dialog" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <Eligibility
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogLoading="$refs.dialogLoading"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :listEligibility="eligibilityList"
          :inforMsg="QUOTE_ELIGIBILITY_INFO_MSG"
          :title="title"
          @confirmEligibility="createQuote()"
          :customHandleConfirmEligibility="true"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogLoading" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ title }">
        <EligibilityLoading
          :gettingQuoteMessage="GETTING_QUOTE_LANDLORD_MESSAGE"
          :title="title"
          :imageSource="defaqtoImageUrl"
        />
      </template>
    </EligibilityBaseDialog>

    <EligibilityBaseDialog ref="dialogNotMet" :windowClass="'confirm-dialog'">
      <template v-slot:content="{ cancel, isShow, title }">
        <EligibilityNotMet
          :cancel="cancel"
          :dialogRef="$refs.dialog"
          :dialogNotMet="$refs.dialogNotMet"
          :isShow="isShow"
          :title="title"
        />
      </template>
    </EligibilityBaseDialog>
  </div>
</template>

<script>
import BaseToggleButton from "@/components/BaseComponents/BaseToggleButton";
import BaseButton from "@/components/BaseComponents/BaseButton";
import BaseSelect from "@/components/BaseComponents/BaseSelect";
import BaseLabel from "@/components/BaseComponents/BaseLabel";
import BaseRadio from "@/components/BaseComponents/BaseRadio";
import BaseInput from "@/components/BaseComponents/BaseInput";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { ValidationObserver } from "vee-validate";
import { mapGetters, mapActions, mapState } from "vuex";
import { quoteResponseToState } from "@/common/quotes";
import { PRICED } from "@/constants/common.constants";
import {
  setItemDataUiids,
  NO_CLAIMS_BUILDINGS_ITEMS,
  NO_CLAIMS_CONTENTS_ITEMS,
  ALARM_TYPE_ITEMS,
} from "@/constants/component.constants.js";
import EligibilityLoading from "@/views/Dialog/EligibilityLoading";
import EligibilityNotMet from "@/views/Dialog/EligibilityNotMet.vue";
import EligibilityBaseDialog from "@/components/EligibilityBaseDialog";
import Eligibility from "@/views/Dialog/Eligibility.vue";
import {
  LANDLORD_ELIGIBILITY,
  B2B_BUSINESS_LANDLORD_ELIGIBILITY,
  QUOTE_ELIGIBILITY_INFO_MSG,
} from "@/constants/eligibility.constants";
import { scrollTop, convertToBool } from "@/common/utils";
import { GETTING_QUOTE_LANDLORD_MESSAGE } from "@/constants/message.constants";
import numeral from "numeral";
import { dereference } from "@/common/transforms";
import LayoutAddressSearch from "@/components/LayoutComponents/LayoutAddressSearch.vue";
import * as LABEL_CONSTANTS from "@/constants/label.constants";

export default {
  name: "PropertyDetailsPage",
  components: {
    ValidationObserver,
    BaseButton,
    BaseSelect,
    BaseToggleButton,
    BaseLabel,
    BaseRadio,
    BaseInput,
    LayoutAddressSearch,
    EligibilityLoading,
    EligibilityNotMet,
    EligibilityBaseDialog,
    Eligibility,
  },
  data() {
    return {
      defaqtoImageUrl: require("@/assets/images/defaqto/landlord-home-insurance.svg"),
      setItemDataUiids,
      ALARM_TYPE_ITEMS,
      windowLocksFitted: null,
      alarmType: null,
      unoccupiedNinetyDays: null,
      nonRoutineBuildingWork: null,
      nonRoutineBuildingWorkValue: null,
      nonRoutineBuildingWorkJctContract: null,
      studentTenants: null,
      dssTenants: null,
      noClaimsBuildings: null,
      noClaimsContents: null,
      insuredAddress: {},
      propertyDetails: {},
      LANDLORD_ELIGIBILITY,
      B2B_BUSINESS_LANDLORD_ELIGIBILITY,
      QUOTE_ELIGIBILITY_INFO_MSG,
      GETTING_QUOTE_LANDLORD_MESSAGE,
      B2B_LANDLORD_PROPERTY_LABEL: {
        BUILD_YEAR_HELP_TEXT: LABEL_CONSTANTS.LANDLORDS_BUILD_YEAR_HELP_TEXT,
        NUMBER_OF_BEDROOMS_HELP_TEXT:
          LABEL_CONSTANTS.NUMBER_OF_BEDROOMS_HELP_TEXT,
        ROOF_CONSTRUCTION_HELP_TEXT:
          LABEL_CONSTANTS.LANDLORDS_ROOF_CONSTRUCTION_HELP_TEXT,
        WALL_CONSTRUCTION_HELP_TEXT:
          LABEL_CONSTANTS.LANDLORDS_WALL_CONSTRUCTION_HELP_TEXT,
      },
    };
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "allSections",
      "httpConfigAsRqs",
      "getQuoteRequestId",
      "propertyDetailsAnswer",
    ]),
    ...mapState(QUESTIONS_MODULE, ["questionMap"]),
    eligibilityList() {
      const insuredParty = this.questionMap.InsuredParty;

      if (insuredParty.Value && insuredParty.Value === "Individual") {
        return this.LANDLORD_ELIGIBILITY;
      } else {
        return this.B2B_BUSINESS_LANDLORD_ELIGIBILITY;
      }
    },
    isBuildingsOnly() {
      return this.questionMap.CoverType.Value === "BuildingsOnly";
    },
    NO_CLAIMS_BUILDINGS_ITEMS() {
      if (!this.questionMap.PreviousLandlordsCover.Value) {
        NO_CLAIMS_BUILDINGS_ITEMS.forEach((item) => {
          if (item.value !== "Two") {
            item.disabled = true;
          }
        });
      } else {
        NO_CLAIMS_BUILDINGS_ITEMS.forEach((item) => (item.disabled = false));
      }
      return NO_CLAIMS_BUILDINGS_ITEMS;
    },
    NO_CLAIMS_CONTENTS_ITEMS() {
      if (!this.questionMap.PreviousLandlordsCover.Value) {
        NO_CLAIMS_CONTENTS_ITEMS.forEach((item) => {
          if (item.value !== "Two") {
            item.disabled = true;
          }
        });
      } else {
        NO_CLAIMS_CONTENTS_ITEMS.forEach((item) => (item.disabled = false));
      }
      return NO_CLAIMS_CONTENTS_ITEMS;
    },
    disableContinue() {
      return (
        this.propertyDetailsAnswer.NumberOfBathrooms === "NinePlus" ||
        this.propertyDetailsAnswer.NumberOfBedrooms === "NinePlus"
      );
    },
  },
  watch: {
    "nonRoutineBuildingWork.Value"(value) {
      if (value !== "true" && value !== true) {
        this.nonRoutineBuildingWorkValue.Value = "";
        this.nonRoutineBuildingWorkJctContract.Value = null;
      } else {
        if (this.nonRoutineBuildingWorkJctContract.Value == null) {
          this.nonRoutineBuildingWorkJctContract.Value = false;
        }
      }
    },
    "nonRoutineBuildingWorkValue.Value"(value) {
      this.questionMap.NonRoutineBuildingWorkValue.Value =
        numeral(value).value();
    },
  },
  created() {
    if (this.allSections.length) {
      this.windowLocksFitted = this.questionMap.WindowLocksFitted;
      this.alarmType = this.questionMap.AlarmType;
      this.unoccupiedNinetyDays = this.questionMap.UnoccupiedNinetyDays;
      this.nonRoutineBuildingWork = this.questionMap.NonRoutineBuildingWork;
      this.nonRoutineBuildingWorkValue = dereference(
        this.questionMap.NonRoutineBuildingWorkValue
      );
      this.nonRoutineBuildingWorkValue.Value =
        this.nonRoutineBuildingWorkValue.Value?.toString() ?? "";
      this.nonRoutineBuildingWorkJctContract =
        this.questionMap.NonRoutineBuildingWorkJctContract;
      this.studentTenants = this.questionMap.StudentTenants;
      this.dssTenants = this.questionMap.DssTenants;
      this.noClaimsBuildings = this.questionMap.NoClaimsBuildings;
      this.noClaimsContents = this.questionMap.NoClaimsContents;

      // insured address
      this.insuredAddress.Street = this.questionMap.InsuredAddressStreet;
      this.insuredAddress.District = this.questionMap.InsuredAddressDistrict;
      this.insuredAddress.Town = this.questionMap.InsuredAddressTown;
      this.insuredAddress.County = this.questionMap.InsuredAddressCounty;
      this.insuredAddress.PostCode = this.questionMap.InsuredAddressPostCode;

      // property details
      this.propertyDetails.PropertyType = this.questionMap.PropertyType;
      this.propertyDetails.BuildYear = this.questionMap.BuildYear;

      this.propertyDetails.NumberOfBedrooms = this.questionMap.NumberOfBedrooms;
      if (this.propertyDetails.NumberOfBedrooms.Value === "")
        this.propertyDetails.NumberOfBedrooms.Value = "One"; //Default to "One" as the UI is set to 1 on page load

      this.propertyDetails.NumberOfBathrooms =
        this.questionMap.NumberOfBathrooms;
      if (this.propertyDetails.NumberOfBathrooms.Value === "")
        this.propertyDetails.NumberOfBathrooms.Value = "One"; //Default to "One" as the UI is set to 1 on page load

      this.propertyDetails.WallConstruction = this.questionMap.WallConstruction;
      this.propertyDetails.RoofConstruction = this.questionMap.RoofConstruction;

      // when retrieving a quote convert "Boolean Strings" to Booleans
      this.windowLocksFitted.Value = convertToBool(
        this.windowLocksFitted.Value
      );
      this.unoccupiedNinetyDays.Value = convertToBool(
        this.unoccupiedNinetyDays.Value
      );
      this.nonRoutineBuildingWork.Value = convertToBool(
        this.nonRoutineBuildingWork.Value
      );
      this.nonRoutineBuildingWorkJctContract.Value = convertToBool(
        this.nonRoutineBuildingWorkJctContract.Value
      );
      this.studentTenants.Value = convertToBool(this.studentTenants.Value);
      this.dssTenants.Value = convertToBool(this.dssTenants.Value);

      if (!this.questionMap.PreviousLandlordsCover.Value) {
        this.noClaimsContents.Value = "Two";
        this.noClaimsBuildings.Value = "Two";
      }
    }
  },
  mounted() {
    scrollTop();
    // Can't go forward automatically from this page - have to submit quotes
    this.setRestoringLastStep(false);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "getQuotes",
      "cloneQuote",
      "setIsExpiredFlag",
      "setRestoringLastStep",
      "toggleGlobalSaveAndExitDialog",
    ]),
    async onSubmit(validate) {
      const isFormValid = await validate();
      if (!isFormValid) {
        const el = this.$el.querySelector(".field-invalid:first-of-type");
        this.$vuetify.goTo(el, { offset: 50 });
        return;
      }

      this.$refs.dialog.open("Eligibility check");
    },
    createQuote() {
      this.questionMap.LandlordsEligibility.Value = true;
      this.$refs.dialog.cancel();
      this.$refs.dialogLoading.open("Hang tight, getting quotes...");
      const quoteRequestFields = {
        IsPartialQuote: false,
      };

      this.getQuotes(quoteRequestFields)
        .then((quoteResponse) => {
          const newState = quoteResponseToState(quoteResponse);
          this.setIsExpiredFlag(false);
          if (newState === PRICED) {
            // Go forward if it got prices
            // Otherwise (UNABLETOQUOTE, etc.) stay on page to show error bars
            // isExpired is always false once a new quote is obtained.
            this.setIsExpiredFlag(false);
            this.$router.push({ name: "YourQuotesPage" });
          } else {
            // If the quote was impossible, it's now read-only in the API side
            // and we need to create a clone for the user to fix and re-submit
            this.cloneQuote();
          }
        })
        .finally(() => {
          if (this.$refs.dialogLoading !== undefined) {
            this.$refs.dialogLoading.cancel();
          }
          if (this.$refs.dialog !== undefined) {
            this.$refs.dialog.cancel();
          }
        });
    },
  },
};
</script>
